/**
 * Photos above the upload limit (10 MB, photoFileValidation.ts) are not rejected
 * but downscaled in the browser: re-encoded as JPEG with the longest side at
 * 4096 px (then 3072 / 2048 px if that is still too large). Phone photos are
 * 4032 px wide, so a normal photo keeps its full resolution and only loses
 * container overhead. A photo that cannot be decoded or shrunk is returned
 * unchanged and then rejected by the size validation.
 */

import { convertHeicFiles } from './heicConversion';

const DOWNSCALABLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_EDGES_PX = [4096, 3072, 2048] as const;
const JPEG_QUALITY = 0.85;

/** True when the photo is over the limit but can be shrunk in the browser. */
export function canDownscale(file: File, maxBytes: number): boolean {
  return file.size > maxBytes && DOWNSCALABLE_TYPES.has(file.type);
}

function toJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}

/** Shrinks a photo below `maxBytes` ("IMG_1.png" → "IMG_1.jpg"); returns it unchanged when that is not possible. */
export async function downscalePhoto(file: File, maxBytes: number): Promise<File> {
  if (!canDownscale(file, maxBytes)) return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    for (const edge of MAX_EDGES_PX) {
      const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await toJpegBlob(canvas);
      if (blob && blob.size <= maxBytes) {
        const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
      }
    }
    return file;
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}

/**
 * Prepares a selection for upload: HEIC → JPEG, then photos over the limit are
 * downscaled. Keeps the order; never throws (files that cannot be prepared are
 * returned unchanged and rejected by the validation).
 */
export async function preparePhotoFiles(files: readonly File[], maxBytes: number): Promise<File[]> {
  const converted = await convertHeicFiles(files);
  return Promise.all(converted.map((file) => downscalePhoto(file, maxBytes)));
}
